<template>
  <b-dropdown
    ref="filtersDropdownElement"
    aria-role="list"
    class="column-filters"
  >
    <template #trigger>
      <b-icon
        :icon="filterIcon"
      />
    </template>
    <b-dropdown-item
      aria-role="listitem"
      custom
    >
      <template v-if="isSortable">
        <b-dropdown-item
          aria-role="listitem"
          class="is-flex is-justify-content-space-between is-align-items-center custom-dropdown-item"
          @click="handleSorting('asc')"
        >
          A → Z sortieren 
        </b-dropdown-item>
        <b-dropdown-item
          aria-role="listitem"
          class="is-flex is-justify-content-space-between is-align-items-center custom-dropdown-item"
          @click="handleSorting('desc')"
        >
          Z → A sortieren 
        </b-dropdown-item>
        <b-dropdown-item
          aria-role="listitem"
          class="is-flex is-justify-content-space-between is-align-items-center custom-dropdown-item"
          @click="handleNumericSorting('asc')"
        >
          0 → 9 sortieren
        </b-dropdown-item>
        <b-dropdown-item
          aria-role="listitem"
          class="is-flex is-justify-content-space-between is-align-items-center custom-dropdown-item"
          @click="handleNumericSorting('desc')"
        >
          9 → 0 sortieren
        </b-dropdown-item>
        <hr class="my-2">
      </template>
      <template v-if="isFilterable">
        <b-collapse
          v-model="isConditionSelectOpen"
          aria-id="contentIdForA11y2"
          class="custom-panel panel mb-0"
          animation="slide"
        >
          <template #trigger>
            <div
              role="button"
              aria-controls="contentIdForA11y2"
              class="pl-0 py-1 is-flex is-align-items-center custom-filter-accordian"
              :aria-expanded="isConditionSelectOpen"
            >
              <b-icon
                :icon="isConditionSelectOpen ? 'menu-down' : 'menu-right'"
                class="mr-1"
              /> Nach Bedingung filtern 
            </div>
          </template>
          <div class="pt-1 px-3 pb-2">
            <b-select
              v-model="columnFilters.condition.type"
              class="my-2"
              expanded
              size="is-small"
              @input="handleConditionUpdate($event)"
            >
              <option
                v-for="(choice, cIndex) in conditionOptions"
                :key="cIndex"
                :value="choice.value"
              >
                {{ choice.label }}
              </option>
            </b-select>
            <b-select
              v-if="columnFilters.condition.type === 'date'"
              v-model="columnFilters.condition.subType"
              class="date-conditions-dropdown"
              size="is-small"
              expanded
              @input="handleDateConditionUpdate"
            >
              <option
                v-for="(choice, cIndex) in dateConditions"
                :key="cIndex"
                :value="choice.value"
              >
                {{ choice.label }}
              </option>
            </b-select>
            <div
              v-if="columnFilters.condition.type !== 'none' && columnFilters.condition.subType"
              class="px-1 date-value-field"
            >
              <b-field
                label="Datum"
              >
                <BaseInput
                  v-model="columnFilters.condition.value"
                  size="is-small"
                  placeholder="2022-01-02"
                />
              </b-field>
            </div>
          </div>
        </b-collapse>
        <b-collapse
          v-model="isValueSelectOpen"
          aria-id="contentIdForA11y2"
          class="custom-panel panel mb-0"
          animation="slide"
          @open="onValueSelectOpen"
        >
          <template #trigger>
            <div
              role="button"
              aria-controls="contentIdForA11y2"
              class="pl-0 py-1 is-flex is-align-items-center custom-filter-accordian"
              :aria-expanded="isValueSelectOpen"
            >
              <b-icon
                :icon="isValueSelectOpen ? 'menu-down' : 'menu-right'"
                class="mr-1"
              /> Nach Werten filtern
            </div>
          </template>
          <div class="p-1 px-3">
            <BaseInput
              v-model="filterKeyword"
              size="is-small"
              class="mt-2"
              type="text"
            />
            <div
              ref="multiSelectContainer"
              class="multi-select"
              @scroll="handleScroll"
            >
              <div
                v-if="isLoadingSuggestions && apiSuggestions.length === 0"
                class="py-2 has-text-centered"
              >
                <b-loading :is-full-page="false" v-model="isLoadingSuggestions"></b-loading>
                Lädt...
              </div>
              <div
                v-for="(option, index) in keywordOptions"
                :key="`option_${index}`"
                class="py-2 is-flex is-align-items-center is-justify-content-start"
                :class="columnFilters.text.keywords.includes(option) ? 'pl-0' : 'pl-5'"
                @click="onOptionSelect(option)"
              >
                <b-icon
                  v-if="columnFilters.text.keywords.includes(option)"
                  icon="check"
                  size="is-small"
                  class="mr-2 ml-0"
                  type="is-primary"
                /> {{ option }}
              </div>
              <div
                v-if="isLoadingSuggestions && apiSuggestions.length > 0"
                class="py-2 has-text-centered"
              >
                <b-icon icon="loading" class="fa-spin"></b-icon>
              </div>
              <div
                v-else-if="hasMoreSuggestions && !isLoadingSuggestions && databaseName && tableName"
                class="py-2 has-text-centered has-text-grey"
              >
                Scrollen Sie für weitere Optionen
              </div>
            </div>
          </div>
        </b-collapse>
      </template>
    </b-dropdown-item>
    <hr class="my-2">
    <b-dropdown-item
      aria-role="listitem"
      custom
    >
      <div class="is-flex is-justify-content-space-between">
        <b-button 
          size="is-small"
          type="is-secondary"
          @click="clearFilter"
        >
          Filter löschen
        </b-button>
        <b-button 
          size="is-small"
          type="is-primary"
          @click="handleFiltering"
        >
          Ok
        </b-button>
      </div>
    </b-dropdown-item>
  </b-dropdown>
</template>

<script >
import { ref, computed, onMounted } from '@vue/composition-api';
import { cloneDeep } from 'lodash';
import { debouncedWatch } from '@vueuse/core';
import BaseInput from '@/components/generics/BaseInput.vue';
import { authInterceptor, createHttpServiceConfig } from '@/services/httpFactories';
import { DATABASE_SERVICE_ENDPOINT, RECORD_RESOURCE_PATH } from '@/services/database-service/databaseServiceConstants';
const __sfc_main = {};
__sfc_main.props = {
  filterIcon: {
    type: String,
    default: 'filter-variant'
  },
  columnData: {
    type: Array,
    default: () => []
  },
  columnFilter: {
    type: Object,
    default: () => {
      return {
        condition: {
          type: 'none',
          subType: '',
          value: ''
        },
        text: {
          keywords: []
        }
      };
    }
  },
  isFilterable: {
    type: Boolean,
    required: true
  },
  isSortable: {
    type: Boolean,
    required: true
  },
  databaseName: {
    type: String,
    required: true
  },
  tableName: {
    type: String,
    required: true
  },
  columnName: {
    type: String,
    required: true
  },
  tableCondition: {
    type: Object,
    default: null
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;
  const conditionOptions = [{
    label: 'Keine',
    value: 'none'
  }, {
    label: 'Datum',
    value: 'date'
  }];
  const dateConditions = [{
    label: 'Datum liegt vor dem',
    value: 'before'
  }, {
    label: 'Datum liegt nach dem',
    value: 'after'
  }, {
    label: 'Datum ist der',
    value: 'exactly'
  }];
  const filterKeyword = ref('');
  const filtersDropdownElement = ref(null);
  const isConditionSelectOpen = ref(false);
  const isValueSelectOpen = ref(false);
  const multiSelectContainer = ref(null);
  const columnFilters = ref({
    condition: {
      type: 'none',
      subType: '',
      value: ''
    },
    text: {
      keywords: []
    }
  });

  // API integration state
  const apiSuggestions = ref([]);
  const isLoadingSuggestions = ref(false);
  const currentOffset = ref(0);
  const totalCount = ref(0);
  const hasMoreSuggestions = ref(true);
  const hasFetchedInitial = ref(false);
  const hasBlankValuesFromAPI = ref(false);
  const suggestionsLimit = 20;

  // Create HTTP service for suggestions
  const suggestionServiceConfig = createHttpServiceConfig(DATABASE_SERVICE_ENDPOINT, `${RECORD_RESOURCE_PATH}/suggestions`);
  suggestionServiceConfig.addRequestInterceptor(authInterceptor);
  onMounted(() => {
    columnFilters.value = cloneDeep(props.columnFilter);
  });
  const keywordOptions = computed(() => {
    // Check if we have database/table names (indicates backend pagination mode)
    const hasBackendPagination = props.databaseName && props.tableName;
    let options = [];
    if (hasBackendPagination) {
      // Backend pagination: use API suggestions
      options = [...apiSuggestions.value];

      // Add "(Leer)" option based on API flag
      if (hasBlankValuesFromAPI.value && !options.includes('(Leer)')) {
        options.unshift('(Leer)');
      }
    } else {
      // Frontend pagination: use local column data
      const uniqueValues = Array.from(new Set(props.columnData.filter(data => data)));
      options = [...uniqueValues];

      // Add "(Leer)" option if there are blank values in local data
      const hasBlanks = props.columnData.some(data => !data);
      if (hasBlanks && !options.includes('(Leer)')) {
        options.unshift('(Leer)');
      }
    }

    // Apply keyword filter if any
    return filterKeyword.value ? options.filter(option => option.toLowerCase().includes(filterKeyword.value.toLowerCase())) : options;
  });
  const handleSorting = sortOrder => {
    emit('sort', {
      sortOrder
    });
  };
  const handleNumericSorting = sortOrder => {
    emit('numericSort', {
      sortOrder
    });
  };
  const handleFiltering = () => {
    emit('filter', cloneDeep(columnFilters.value));
    filtersDropdownElement.value.isActive = false;
  };
  const clearFilter = () => {
    columnFilters.value = {
      ...columnFilters.value,
      ...{
        condition: {
          type: '',
          subType: '',
          value: ''
        },
        text: {
          keywords: []
        }
      }
    };
    filterKeyword.value = '';
    handleFiltering();
  };
  const onOptionSelect = option => {
    const keywords = columnFilters.value.text.keywords;
    if (keywords.includes(option)) {
      keywords.splice(keywords.indexOf(option), 1);
    } else {
      keywords.push(option);
    }
  };
  const handleConditionUpdate = option => {
    columnFilters.value.condition.subType = option === '' ? null : 'before';
    columnFilters.value.condition.value = null;
  };
  const handleDateConditionUpdate = () => {
    columnFilters.value.condition.value = null;
  };

  // API functions
  const loadSuggestions = async (reset = false) => {
    if (isLoadingSuggestions.value || !hasMoreSuggestions.value && !reset) {
      return;
    }
    try {
      isLoadingSuggestions.value = true;
      if (reset) {
        apiSuggestions.value = [];
        currentOffset.value = 0;
        hasMoreSuggestions.value = true;
      }
      const query = {
        database: {
          name: props.databaseName
        },
        table: {
          name: props.tableName
        },
        column: props.columnName,
        keyword: filterKeyword.value || '',
        limit: suggestionsLimit,
        offset: currentOffset.value
      };

      // Include table condition if provided
      if (props.tableCondition) {
        query.condition = props.tableCondition;
      }
      const response = await suggestionServiceConfig.http({
        method: 'GET',
        params: {
          query: JSON.stringify(query)
        }
      });
      const responseData = response.data?.data || {};
      const newSuggestions = responseData.suggestions || [];
      totalCount.value = responseData.total_count || 0;
      hasMoreSuggestions.value = responseData.has_more || false;
      hasBlankValuesFromAPI.value = responseData.has_blank_values || false;
      if (reset) {
        apiSuggestions.value = newSuggestions;
      } else {
        // Append new suggestions for pagination
        apiSuggestions.value = [...apiSuggestions.value, ...newSuggestions];
      }

      // Update offset for next page
      currentOffset.value += newSuggestions.length;
    } catch (error) {
      console.error('Error loading suggestions:', error);
      hasMoreSuggestions.value = false;
    } finally {
      isLoadingSuggestions.value = false;
      hasFetchedInitial.value = true;
    }
  };

  // Handle scroll for lazy loading
  const handleScroll = async event => {
    // Only handle scroll loading for backend pagination mode
    const hasBackendPagination = props.databaseName && props.tableName;
    if (!hasBackendPagination) return;
    const container = event.target;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;

    // Check if scrolled near bottom (within 50px) and can load more
    if (scrollTop + clientHeight >= scrollHeight - 50 && hasMoreSuggestions.value && !isLoadingSuggestions.value) {
      // Store current scroll position
      const currentScrollTop = scrollTop;
      await loadSuggestions(false);

      // Restore scroll position after loading
      requestAnimationFrame(() => {
        if (container) {
          container.scrollTop = currentScrollTop;
        }
      });
    }
  };

  // Function to handle when value select dropdown opens
  const onValueSelectOpen = () => {
    // Only load suggestions if we're in backend pagination mode
    const hasBackendPagination = props.databaseName && props.tableName;
    if (hasBackendPagination && !hasFetchedInitial.value) {
      loadSuggestions(true);
    }
  };

  // Debounced watch for filter keyword changes
  debouncedWatch(filterKeyword, () => {
    // Only load suggestions if we're in backend pagination mode
    const hasBackendPagination = props.databaseName && props.tableName;
    if (hasBackendPagination && hasFetchedInitial.value) {
      loadSuggestions(true);
    }
  }, {
    debounce: 300
  });
  return {
    conditionOptions,
    dateConditions,
    filterKeyword,
    filtersDropdownElement,
    isConditionSelectOpen,
    isValueSelectOpen,
    multiSelectContainer,
    columnFilters,
    apiSuggestions,
    isLoadingSuggestions,
    hasMoreSuggestions,
    keywordOptions,
    handleSorting,
    handleNumericSorting,
    handleFiltering,
    clearFilter,
    onOptionSelect,
    handleConditionUpdate,
    handleDateConditionUpdate,
    handleScroll,
    onValueSelectOpen
  };
};
__sfc_main.components = Object.assign({
  BaseInput
}, __sfc_main.components);
export default __sfc_main;
</script>

<style lang="scss">
.column-filters {
  .dropdown-menu {
    width: 300px;
    .input, .button {
      height: 2rem !important;
    }
  }
  .multi-select{
    height: 8.75rem; 
    overflow-y: auto;
    width: 100%;
    margin-top: 1rem;
    cursor: pointer;
    padding: 0rem;
    font-weight: 400;
    position: relative;
  }
  .custom-dropdown-item{
    padding-left: 2.50rem;
  }
  .custom-panel{
    &.panel{
      box-shadow: none;
      font-size: inherit;
      background: inherit;
      padding: 2px 0;
    }
  }
  .date-value-field {
    margin-top: 0.5rem
  }
}
</style>
